import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Star, Quote } from 'lucide-react';

const getTestimonialsData = (t) => [
  {
    id: 'testimonial-1',
    name: 'Sarah Johnson',
    role: t('home.testimonials.role1'),
    company: 'EuroFoods Ltd',
    country: t('home.testimonials.country1'),
    content: t('home.testimonials.content1'),
    rating: 5,
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop'
  },
  {
    id: 'testimonial-2',
    name: 'Kwame Asante',
    role: t('home.testimonials.role2'),
    company: 'Ghana Exports Co.',
    country: t('home.testimonials.country2'),
    content: t('home.testimonials.content2'),
    rating: 5,
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop'
  },
  {
    id: 'testimonial-3',
    name: 'Maria Rodriguez',
    role: t('home.testimonials.role3'),
    company: 'GlobalTrade Inc.',
    country: t('home.testimonials.country3'),
    content: t('home.testimonials.content3'),
    rating: 5,
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop'
  }
];

export const TestimonialsSection = () => {
  const { t } = useTranslation();
  const testimonials = getTestimonialsData(t);
  
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C25E30] mb-3">
            {t('home.testimonials.badge')}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#0A0A0A] tracking-tight mb-4">
            {t('home.testimonials.title')}
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            {t('home.testimonials.subtitle')}
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((t, idx) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="relative bg-slate-50 p-8 rounded-3xl hover:bg-white hover:shadow-xl transition-all duration-300 border border-slate-100 group"
            >
              {/* Quote Icon */}
              <div className="absolute -top-4 left-8">
                <div className="w-10 h-10 bg-[#C25E30] rounded-xl flex items-center justify-center shadow-lg shadow-[#C25E30]/30">
                  <Quote className="w-5 h-5 text-white" />
                </div>
              </div>
              
              {/* Rating Stars */}
              <div className="flex gap-1 mb-5 pt-4">
                {[...Array(t.rating)].map((_, i) => (
                  <Star key={`${t.id}-star-${i}`} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              
              {/* Content */}
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                "{t.content}"
              </p>
              
              {/* Author */}
              <div className="flex items-center gap-4">
                <img 
                  src={t.avatar} 
                  alt={t.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-md"
                />
                <div>
                  <p className="font-semibold text-[#0A0A0A]">{t.name}</p>
                  <p className="text-sm text-slate-500">{t.role}, {t.company}</p>
                  <p className="text-xs text-[#C25E30] font-medium">{t.country}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
        >
          {[
            { value: '4.9/5', labelKey: 'home.testimonials.avgRating' },
            { value: '10K+', labelKey: 'home.testimonials.happyUsers' },
            { value: '$2.5B+', labelKey: 'home.testimonials.tradeVolume' },
            { value: '98%', labelKey: 'home.testimonials.satisfaction' },
          ].map((stat, idx) => (
            <div key={stat.labelKey} className="p-4">
              <p className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-1">{stat.value}</p>
              <p className="text-slate-500 text-sm">{t(stat.labelKey)}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
